import * as React from "react";

// We don't use Link here to avoid circular imports and breaking HMR for Card
// and other components. So we sort of rebuild Link manually.
// import {Link} from "@client/router";

import {zIndexClasses} from "@client/style";

import {Highlight} from "@client/components/Highlight";
import {Locator, handleOnClickWithoutHijack} from "@client/gotterdammerung";
import * as hardware from "@client/hardware";
import {useSiteContext} from "@client/site/context";
import {classNames} from "@reactivated";

const styles = {
    touchHandler: `touch-handler cursor-pointer relative [&_canvas]:text-[#EF8165] [&_canvas]:${zIndexClasses.touchHandler} [&_canvas]:cursor-pointer`,
    blend: "touch-handler-blend [&_canvas]:!text-current",
} as const;

interface Props {
    haptic?: boolean;
    onClick?: () => void;
    role?: "tab" | "button" | "link";
    ariaLabel?: string;
    disableBackground?: true;
    blend?: true;
    highlight?: boolean;
    link?: string | (() => void) | null | Locator | {linkFromAppToSite: string};
    children?: React.ReactNode;
    className?: string;
}

export const TouchHandler = (props: Props) => {
    const {realm} = useSiteContext();
    const effects = <>{props.highlight === true ? <Highlight /> : null}</>;
    const classes = classNames(
        styles.touchHandler,
        props.className,
        props.blend && styles.blend,
    );

    if (typeof props.link === "string") {
        if (realm === "site") {
            return (
                <a
                    role={props.role}
                    aria-label={props.ariaLabel}
                    className={classes}
                    onClick={props.onClick}
                    href={props.link}
                >
                    {effects}
                    {props.children}
                </a>
            );
        }
        const {link} = props;

        return (
            <a
                role={props.role}
                aria-label={props.ariaLabel}
                className={classes}
                onClick={(event) =>
                    handleOnClickWithoutHijack(event, {
                        alwaysRuns: props.onClick,
                        callback: async () => {
                            const router = await import("@client/router");
                            void router.unsafeTransitionByUrl(link, "push");
                        },
                    })
                }
                href={props.link}
            >
                {effects}
                {props.children}
            </a>
        );
    } else if (typeof props.link === "function") {
        if (props.onClick != null) {
            throw new Error("Cannot have a dynamic link and a click handler");
        }
        const link = props.link;
        return (
            <a
                role={props.role}
                aria-label={props.ariaLabel}
                className={classes}
                onClick={() => {
                    if (props.haptic == true) {
                        void hardware.haptic();
                    }
                    link();
                }}
            >
                {effects}
                {props.children}
            </a>
        );
    } else if (props.link != null && "nominal" in props.link) {
        const locator = props.link;

        return (
            <a
                role={props.role}
                aria-label={props.ariaLabel}
                className={classes}
                href={locator.url}
                onClick={(event) => {
                    handleOnClickWithoutHijack(event, {
                        alwaysRuns: props.onClick,
                        callback: async () => {
                            if (props.haptic == true) {
                                void hardware.haptic();
                            }
                            const router = await import("@client/router");
                            void router.transition(locator);
                        },
                    });
                }}
            >
                {effects}
                {props.children}
            </a>
        );
    } else if (props.link != null && "linkFromAppToSite" in props.link) {
        return (
            <a
                role={props.role}
                aria-label={props.ariaLabel}
                className={classes}
                onClick={props.onClick}
                href={props.link.linkFromAppToSite}
            >
                {effects}
                {props.children}
            </a>
        );
    } else if (props.onClick != null) {
        return (
            <a
                role={props.role}
                aria-label={props.ariaLabel}
                className={classes}
                onClick={props.onClick}
            >
                {effects}
                {props.children}
            </a>
        );
    }

    return <span className={classes}>{props.children}</span>;
};
